import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { TableWrap, Table, THead, TH, TR, TD } from '@/components/ui/table';
import { apiMessage } from '../shared';
import { addDecimal, isSignedDecimal, multiplyDecimal, sumDecimal } from './signedDecimal';

export interface SupplierOption {
  id: string;
  name: string;
  code: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address?: string | null;
  paymentTerms: string | null;
  isActive: boolean;
}

export interface PurchaseItemOption {
  id: string;
  name: string;
  sku: string | null;
  unitId: string;
}

export interface UnitOption {
  id: string;
  code: string;
}

export interface WarehouseOption {
  id: string;
  name: string;
}

interface DraftLine {
  inventoryItemId: string;
  quantity: string;
  unitCost: string;
}

const BLANK_LINE: DraftLine = { inventoryItemId: '', quantity: '', unitCost: '' };

/** §21 — a purchase quantity or cost is unsigned decimal text on the wire. */
const DECIMAL = /^\d+(\.\d+)?$/;

/**
 * Raising a purchase order.
 *
 * ## Three controls the frame draws that are not built, and why
 *
 * - **`Priority`** — no column on `PurchaseOrder`, no field on
 *   `CreatePurchaseOrderDto`, and nothing anywhere sorts or filters orders by
 *   one. Under `forbidNonWhitelisted` sending it 400s the whole create.
 * - **`Delivery SAR 50.00`** — there is no shipping or freight column at header
 *   or line level, and `total` is computed server-side from the lines, so a
 *   client-side delivery fee would make the total on screen disagree with the
 *   total that was stored. A header `Tax` amount is offered in its place:
 *   `PurchaseOrder.taxTotal` is real, is on the DTO, and is the only place a
 *   supplier's VAT can go — `PurchaseOrderLine` has no tax column at all.
 * - **`Approval Workflow` / "Orders over SAR 500 require manager approval"** —
 *   create, update, submit *and* approve are all `inventory.adjust`. There is no
 *   threshold, no approver route and no procurement `PolicyApproval` kind, so
 *   the card would promise something the server does not do. For the same
 *   reason the primary action says what it does — sends the order to the
 *   supplier — rather than "Submit for approval".
 *
 * The PO number is likewise not an input. It is allocated server-side as
 * `PO-{year}-{5 digits}`; sending one 400s, and showing an invented
 * `PO-260822-001` next to "auto-generated" would be a number nobody can find.
 *
 * ## What is kept that the frame drops
 *
 * The `Warehouse` select — "where the goods are expected to land", which
 * `GoodsReceiptsService` inherits when receiving against this order, and
 * `PurchaseReceipt.warehouseId` is required. Dropping it would push the choice
 * onto whoever unloads the van. Per-line delete and the at-least-one-line guard
 * stay too.
 */
export function PurchaseOrderForm({
  suppliers,
  items,
  units,
  warehouses,
  onCancel,
  onSaved,
}: {
  suppliers: SupplierOption[];
  items: PurchaseItemOption[];
  units: UnitOption[];
  warehouses: WarehouseOption[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { format: money } = useCurrency();

  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [taxTotal, setTaxTotal] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{ ...BLANK_LINE }]);
  const [busy, setBusy] = useState<string | null>(null);

  const supplier = suppliers.find((s) => s.id === supplierId) ?? null;
  const unitCodeById = useMemo(
    () => new Map(units.map((unit) => [unit.id, unit.code])),
    [units],
  );
  const itemById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  const filledLines = useMemo(
    () => lines.filter((line) => line.inventoryItemId && line.quantity.trim()),
    [lines],
  );

  /** Line totals, and the subtotal the server will independently compute from
   *  the same numbers. Decimal text throughout — a 4dp unit cost times a 3dp
   *  quantity is exactly the product a double rounds. */
  const lineTotals = useMemo(
    () =>
      lines.map((line) =>
        DECIMAL.test(line.quantity.trim()) && DECIMAL.test(line.unitCost.trim())
          ? multiplyDecimal(line.quantity.trim(), line.unitCost.trim())
          : null,
      ),
    [lines],
  );

  const subtotal = useMemo(
    () => sumDecimal(lineTotals.filter((total): total is string => total !== null)),
    [lineTotals],
  );

  const total = useMemo(
    () => (isSignedDecimal(taxTotal.trim()) ? addDecimal(subtotal, taxTotal.trim()) : subtotal),
    [subtotal, taxTotal],
  );

  const error = useMemo(() => {
    if (!supplierId) return t('platform.procurement.chooseSupplier');
    if (!filledLines.length) return t('platform.procurement.orderNeedsLines');
    if (filledLines.some((l) => !DECIMAL.test(l.quantity.trim()) || Number(l.quantity) <= 0)) {
      return t('platform.procurement.quantityPositive');
    }
    if (filledLines.some((l) => l.unitCost.trim() && !DECIMAL.test(l.unitCost.trim()))) {
      return t('platform.procurement.costInvalid');
    }
    if (taxTotal.trim() && !DECIMAL.test(taxTotal.trim())) {
      return t('platform.procurement.costInvalid');
    }
    return null;
  }, [filledLines, supplierId, t, taxTotal]);

  const save = async (thenSubmit: boolean) => {
    if (error) return;
    setBusy(thenSubmit ? 'submit' : 'draft');
    try {
      const { data } = await api.post<{ id: string }>('/procurement/purchase-orders', {
        supplierId,
        ...(warehouseId ? { warehouseId } : {}),
        ...(expectedAt ? { expectedAt: new Date(expectedAt).toISOString() } : {}),
        ...(taxTotal.trim() ? { taxTotal: taxTotal.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        lines: filledLines.map((line) => ({
          inventoryItemId: line.inventoryItemId,
          // The digits the buyer typed. Rounding to the branch's minor unit here
          // would silently restate a price quoted in the supplier's own money.
          quantity: line.quantity.trim(),
          ...(line.unitCost.trim() ? { unitCost: line.unitCost.trim() } : {}),
        })),
      });

      if (thenSubmit) {
        // Two requests because there is no create-and-send route, and the second
        // only runs if the first actually produced an order.
        await api.post(`/procurement/purchase-orders/${data.id}/submit`, {});
        toast.success(t('platform.procurement.submitted'));
      } else {
        toast.success(t('platform.procurement.orderCreated'));
      }
      onSaved();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  const patchLine = (index: number, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">{t('platform.procurement.newOrder')}</h2>
        <p className="text-sm text-ink-muted">{t('platform.procurement.newOrderHint')}</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        {/* `min-w-0` on both tracks — a grid item's automatic minimum is its
            min-content width, which the line table would otherwise impose on
            the whole page instead of scrolling inside `TableWrap`. */}
        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('platform.procurement.supplier')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label={t('platform.procurement.supplier')} required>
                <Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                  <option value="">{t('platform.procurement.chooseSupplier')}</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>

              {/* Read straight off the chosen supplier — no second request. The
                  frame's second Payment Terms line ("Bank Transfer") has no
                  column: `paymentTerms` is one free-form string. */}
              {supplier && (
                <dl className="grid gap-4 text-sm sm:grid-cols-3">
                  {(
                    [
                      [
                        t('platform.procurement.contact'),
                        [supplier.contactName, supplier.phone].filter(Boolean).join(' · '),
                      ],
                      [t('platform.procurement.terms'), supplier.paymentTerms],
                      [t('platform.procurement.address'), supplier.address],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{label}</dt>
                      <dd className="mt-0.5">{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between gap-2">
              <CardTitle>{t('platform.procurement.lines')}</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLines((prev) => [...prev, { ...BLANK_LINE }])}
              >
                <Icon as={Plus} />
                {t('platform.procurement.addLine')}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>{t('platform.procurement.item')}</TH>
                      <TH>{t('common.quantity')}</TH>
                      <TH>{t('operations.inventory.unit')}</TH>
                      <TH>{t('platform.procurement.unitCost')}</TH>
                      <TH className="text-end">{t('common.total')}</TH>
                      <TH className="w-px" />
                    </TR>
                  </THead>
                  <tbody>
                    {lines.map((line, index) => {
                      const item = itemById.get(line.inventoryItemId);
                      return (
                        <TR key={index}>
                          <TD className="min-w-56">
                            <Select
                              aria-label={t('platform.procurement.chooseItem')}
                              value={line.inventoryItemId}
                              onChange={(e) =>
                                patchLine(index, { inventoryItemId: e.target.value })
                              }
                            >
                              <option value="">{t('platform.procurement.chooseItem')}</option>
                              {items.map((option) => (
                                <option key={option.id} value={option.id}>
                                  {option.name}
                                  {option.sku ? ` (${option.sku})` : ''}
                                </option>
                              ))}
                            </Select>
                          </TD>
                          <TD className="w-28">
                            <Input
                              inputMode="decimal"
                              aria-label={t('common.quantity')}
                              value={line.quantity}
                              onChange={(e) => patchLine(index, { quantity: e.target.value })}
                            />
                          </TD>
                          {/* The purchase unit defaults to the item's stock unit,
                              which is what the DTO does when `unitId` is omitted. */}
                          <TD className="text-ink-muted">
                            {(item && unitCodeById.get(item.unitId)) ?? '—'}
                          </TD>
                          <TD className="w-32">
                            <Input
                              inputMode="decimal"
                              aria-label={t('platform.procurement.unitCost')}
                              value={line.unitCost}
                              onChange={(e) => patchLine(index, { unitCost: e.target.value })}
                            />
                          </TD>
                          <TD className="text-end tabular-nums">
                            {lineTotals[index] === null ? '—' : money(lineTotals[index])}
                          </TD>
                          <TD>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t('common.delete')}
                              disabled={lines.length === 1}
                              onClick={() =>
                                setLines((prev) => prev.filter((_, i) => i !== index))
                              }
                            >
                              <Icon as={Trash2} />
                            </Button>
                          </TD>
                        </TR>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>

              <dl className="ms-auto w-full max-w-xs space-y-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-ink-muted">{t('common.subtotal')}</dt>
                  <dd className="tabular-nums" data-testid="po-subtotal">
                    {money(subtotal)}
                  </dd>
                </div>
                {/* The only place a supplier's VAT can be recorded: there is no
                    line-level tax column, so a per-line breakdown is impossible. */}
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-ink-muted">{t('common.tax')}</dt>
                  <dd className="w-28">
                    <Input
                      className="h-9 text-end"
                      inputMode="decimal"
                      aria-label={t('common.tax')}
                      placeholder="0.00"
                      value={taxTotal}
                      onChange={(e) => setTaxTotal(e.target.value)}
                    />
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3 border-t pt-2 text-lg font-semibold">
                  <dt>{t('common.total')}</dt>
                  <dd className="tabular-nums">{money(total)}</dd>
                </div>
              </dl>

              {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
            </CardContent>
          </Card>
        </div>

        <Card className="h-fit min-w-0">
          <CardHeader>
            <CardTitle>{t('platform.procurement.order')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                {t('platform.procurement.number')}
              </p>
              {/* Not an input: the number is allocated by the server when the
                  order is created, and sending one 400s the request. */}
              <p className="mt-0.5 text-sm text-ink-muted">
                {t(
                  'platform.procurement.numberAllocated',
                  'Allocated when the order is created, as PO-{{year}}-00001.',
                  { year: new Date().getFullYear() },
                )}
              </p>
            </div>

            <Field
              label={t('platform.procurement.warehouse')}
              hint={t('platform.procurement.warehouseHint')}
            >
              <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                <option value="">—</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label={t('platform.procurement.expected')}>
              <Input
                type="datetime-local"
                value={expectedAt}
                onChange={(e) => setExpectedAt(e.target.value)}
              />
            </Field>

            <Field label={t('platform.procurement.notes')}>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => void save(false)}
            disabled={!!busy || !!error}
            title={error ?? undefined}
          >
            {t('platform.procurement.saveDraft')}
          </Button>
          <Button
            onClick={() => void save(true)}
            disabled={!!busy || !!error}
            title={error ?? undefined}
          >
            {t('platform.procurement.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
